import { useFocusable } from '../../hooks/useFocusable.js';
import '../button/index.js';
import { Arrow } from '../arrow/index.js';
import { SeoSubNav } from './seoSubNav.js';
import { useMenu } from './menuContext.js';
import { ListItem, MenuButton, ButtonText } from './styles.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { BUTTON_VARIANTS } from '../button/constants.js';

const MenuListItem = _ref => {
  let {
    active,
    children,
    divider,
    href,
    icon,
    index,
    name,
    onClick,
    primary,
    rtl,
    to,
    type,
    ...rest
  } = _ref;
  const {
    direction,
    isOpen,
    labels,
    menuData,
    menuLevel,
    options: {
      role,
      variant
    },
    setIsOpen,
    setMenuData,
    setMenuLevel
  } = useMenu();
  const {
    focusNext,
    focusPrevious
  } = useFocusable();
  if (!name) {
    return null;
  }
  const hasChildren = !!(children && children.length);
  const isBackButton = type === 'menuBackButton';
  const isRtl = direction === 'rtl';

  /**
   * If this item has children, generate the next menu panel
   * from them and add it to the next position in menuData array,
   * then move to that level.
   *
   * We trim menuData here (rather than in `focusPrevPanel`) because
   * we want it to remain in view whilst transitioning out.
   */
  const focusNextPanel = () => {
    const newMenuData = [...menuData];
    const menuBackButton = {
      name,
      type: 'menuBackButton'
    };
    const nextPanel = [menuBackButton, ...children];

    // Trim the menu and insert the next panel
    newMenuData.splice(menuLevel + 1, newMenuData.length, nextPanel);
    setMenuData(newMenuData);
    setMenuLevel(menuLevel + 1);
  };
  const focusPrevPanel = () => {
    setMenuLevel(menuLevel - 1);
  };
  const onItemClick = e => {
    hasChildren && focusNextPanel();
    isBackButton && focusPrevPanel();
    typeof onClick === 'function' && onClick(e);
    if (!hasChildren && !isBackButton && !href && !to) setIsOpen(false);
  };

  /**
   * Keyboard focus management. Up/down default prevented to
   * avoid scrolling the page/container.
   */
  const onKeyDown = e => {
    switch (e.key) {
      case 'ArrowDown':
        {
          focusNext();
          e.preventDefault();
          break;
        }
      case 'ArrowUp':
        {
          focusPrevious();
          e.preventDefault();
          break;
        }
      case 'ArrowLeft':
        {
          if (hasChildren && isRtl) {
            focusNextPanel();
          }
          if (isBackButton && !isRtl) {
            focusPrevPanel();
          }
          break;
        }
      case 'ArrowRight':
        {
          if (hasChildren && !isRtl) {
            focusNextPanel();
          }
          if (isBackButton && isRtl) {
            focusPrevPanel();
          }
          break;
        }
    }
  };
  return /*#__PURE__*/jsxs(ListItem, {
    isBackButton: isBackButton,
    divider: divider,
    children: [/*#__PURE__*/jsxs(MenuButton, {
      active: active,
      as: href || to ? 'a' : 'button',
      href: href || to,
      isBackButton: isBackButton,
      isMenuItem: true,
      menuVariant: variant,
      onClick: onItemClick,
      onKeyDown: onKeyDown,
      primary: primary,
      role: role === 'menu' ? 'menuitem' : undefined,
      title: isBackButton ? labels?.back : undefined,
      variant: BUTTON_VARIANTS.PLAIN,
      ...rest,
      children: [isBackButton && /*#__PURE__*/jsx(Arrow, {
        arrowDirection: "backward",
        isRtl: isRtl,
        "aria-hidden": true
      }), icon && icon, /*#__PURE__*/jsx(ButtonText, {
        dir: rtl ? 'rtl' : undefined,
        children: name
      }), hasChildren && /*#__PURE__*/jsx(Arrow, {
        arrowDirection: "forward",
        isRtl: isRtl,
        "aria-hidden": true
      })]
    }), hasChildren && !isOpen ? /*#__PURE__*/jsx(SeoSubNav, {
      data: children
    }) : null]
  });
};

export { MenuListItem };
